import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormCheckbox } from '@/components/form/FormCheckbox'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormSelect } from '@/components/form/FormSelect'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentSkill } from '@/features/student/api/profile.api'
import { useSkillMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'
import { humanizeEnum } from '@/utils/format'

const SKILL_LEVEL_OPTIONS = ['beginner', 'intermediate', 'advanced', 'expert'].map((value) => ({
  value,
  label: humanizeEnum(value),
}))

const schema = z.object({
  skill_name: z.string().min(1, 'Skill name is required'),
  skill_level: z.string().optional(),
  years_of_experience: z.string().optional(),
  is_primary: z.boolean(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentSkill): FormValues {
  return {
    skill_name: item?.skillName ?? '',
    skill_level: item?.skillLevel ?? '',
    years_of_experience: item?.yearsOfExperience != null ? String(item.yearsOfExperience) : '',
    is_primary: item?.isPrimary ?? false,
  }
}

export function SkillSection({ items }: { items: StudentSkill[] }) {
  const [editing, setEditing] = useState<StudentSkill | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useSkillMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const payload = {
      ...values,
      skill_level: values.skill_level || undefined,
      years_of_experience: values.years_of_experience
        ? Number(values.years_of_experience)
        : undefined,
    }
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(payload, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No skills added yet.</p>}
      <div className="d-flex flex-wrap gap-2 mb-3">
        {items.map((item) => (
          <span
            key={item.id}
            className="badge bg-light text-dark border d-flex align-items-center gap-2 p-2"
          >
            {item.skillName}
            {item.skillLevel && (
              <span className="text-muted fs-11">({humanizeEnum(item.skillLevel)})</span>
            )}
            <button
              type="button"
              className="btn-close fs-10"
              aria-label={`Edit ${item.skillName}`}
              onClick={() => setEditing(item)}
            />
          </span>
        ))}
      </div>
      <Button variant="secondary" onClick={() => setEditing('new')}>
        Add Skill
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Skill' : 'Edit Skill'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="skill_name" label="Skill" required />
          <FormSelect
            control={control}
            name="skill_level"
            label="Proficiency"
            options={SKILL_LEVEL_OPTIONS}
          />
          <FormInput
            control={control}
            name="years_of_experience"
            label="Years of Experience"
            type="number"
          />
          <FormCheckbox control={control} name="is_primary" label="Primary skill" />
          <div className="d-flex gap-2">
            <Button type="submit" className="flex-grow-1" loading={isPending}>
              Save
            </Button>
            {editing !== 'new' && editing && (
              <Button
                variant="danger"
                confirmTitle="Remove this skill?"
                onClick={() => remove.mutate(editing.id, { onSuccess: () => setEditing(null) })}
              >
                Delete
              </Button>
            )}
          </div>
        </form>
      </Modal>
    </div>
  )
}
